import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { useSession } from '../../state/useSession';
import { useProjects } from '../../state/projects';
import { isUnfinished, isWebApp } from '@/webapp/webAppRecord';
import type { AppDefinition } from '@/types/app';
import ChatPanel from './ChatPanel';
import PreviewFrame from './PreviewFrame';
import PublishPanel from './PublishPanel';
import UnsupportedRuntime from './UnsupportedRuntime';
import UnfinishedBuild from './UnfinishedBuild';
import SiteLine from './SiteLine';
import ManagePanel from './manage/ManagePanel';
import { Link } from 'react-router';
import { useSiteCheck } from '../../state/siteStatus';
import { accountsIn, addSignInRequest, reachOf, signInSummary } from '../../state/people';

type View = 'chat' | 'preview' | 'manage';

/** `?mode=` as a view, for a link that lands on one: the build lands on Preview. */
function viewFrom(param: string | null): View {
  return param === 'preview' || param === 'manage' ? param : 'chat';
}

/**
 * Chat, Preview and Manage — the phone's three tabs, in its words.
 *
 * Below 1024px they are three views with a toggle, as on the phone. From 1024px Chat and
 * Preview are both on screen at once — the extra width buys a capability instead of
 * rearranging the same one — and Manage replaces the pair, so the toggle stays to get back.
 */
export default function ProjectEditor({ mode }: { mode?: View }) {
  const { id = '' } = useParams();
  const { user } = useSession();
  const [params] = useSearchParams();
  // A new entry on every navigation — including the build's own, which lands on the SAME
  // /project/:id when it fills a reserved row, and must not keep showing the unfinished one.
  const { key: visit } = useLocation();
  const project = useProjects((s) => s.currentProject);
  const [view, setView] = useState<View>(mode ?? viewFrom(params.get('mode')));
  /*
   * Manage is drawn the first time it is opened and then KEPT, hidden, like Chat: a row half
   * typed there survives a look at Preview. Not drawn before that, because its Live section asks
   * the server for the site's rows, and an owner who only came to chat has not asked for them.
   */
  const [manageOpened, setManageOpened] = useState(view === 'manage');
  const choose = (next: View) => {
    setView(next);
    if (next === 'manage') setManageOpened(true);
  };
  // A chat edit on its way, and a Manage save on its way. Each publishes itself when it lands,
  // so Publish waits for both — and Manage will not rewrite the source under a chat edit.
  const [chatBusy, setChatBusy] = useState(false);
  const [manageBusy, setManageBusy] = useState(false);
  const editing = chatBusy || manageBusy;

  /*
   * Words Manage asks to have said in Chat — "Add sign-in" — handed to the composer for the owner
   * to read and send, never sent for them. Numbered, so asking for the same words twice is two
   * requests. The phone's `askInChat` (src/app/(main)/project/[id].tsx).
   */
  const [chatPrefill, setChatPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const asked = useRef(0);
  const askInChat = useCallback((text: string) => {
    asked.current += 1;
    setChatPrefill({ text, nonce: asked.current });
    setView('chat');
  }, []);

  useEffect(() => {
    void useProjects.getState().loadProject(id, user?.id ?? '');
  }, [id, user?.id, visit]);

  /*
   * Whether the page is up, asked of the server once and shown in two places: beside the address
   * and at the top of the Your website panel, which is also what changes it. Asked here, above
   * the early returns, because it is a hook; only an app that can have a page has one to ask about.
   */
  const opened = project && project.id === id ? (project.definition as AppDefinition) : null;
  const pageable = opened && isWebApp(opened) && !isUnfinished(opened) ? opened : null;
  const [site, setSite] = useSiteCheck(pageable?.hostedAppId);

  if (!project || project.id !== id) return <p className="type-body">Loading…</p>;

  const definition = project.definition as AppDefinition;
  if (!isWebApp(definition)) return <UnsupportedRuntime runtime={definition.runtime ?? 'engine'} />;
  // Read by the MARK, never by the source: a gate failure can leave text that is not an app.
  if (isUnfinished(definition)) return <UnfinishedBuild project={project} />;

  const source = definition.source ?? '';
  /*
   * Whether the site has sign-in, in the phone's sentence (state/people.ts). The owner asked how
   * to "enable user management" and found nothing: People lived two screens away under My Apps,
   * and nothing said whether the site had accounts at all.
   */
  const accounts = accountsIn(source);
  const reach = reachOf(site);
  const signIn = signInSummary(
    accounts,
    reach,
    definition.hostedAppId ? 'Everyone who has signed in is listed under People using this site.' : '',
  );

  return (
    <div className="editor">
      <header className="editor-head">
        <div className="editor-title">
          <h1 className="type-title">{project.name}</h1>
          <SiteLine webUrl={definition.webUrl} site={site} />
        </div>
        <div
          className="editor-toggle"
          role="tablist"
          aria-label="Editor view"
        >
          {(['chat', 'preview', 'manage'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className={view === v ? 'is-selected' : ''}
              onClick={() => choose(v)}
            >
              {v === 'chat' ? 'Chat' : v === 'preview' ? 'Preview' : 'Manage'}
            </button>
          ))}
        </div>
      </header>

      {/*
        * `hidden` on a plain wrapper, never on the split or a pane: from 1024px their own
        * display rules would win over the attribute and draw them under Manage. Kept mounted,
        * so a message half typed in Chat is still there after a visit to Manage.
        */}
      <div hidden={view === 'manage'}>
        <div className="editor-split">
          <div className={view === 'chat' ? 'editor-pane' : 'editor-pane is-hidden-narrow'}>
            <ChatPanel
              projectId={id}
              appId={definition.id}
              goal={project.description}
              source={source}
              onBusyChange={setChatBusy}
              prefill={chatPrefill}
            />
          </div>
          <div className={view === 'preview' ? 'editor-pane' : 'editor-pane is-hidden-narrow'}>
            <PreviewFrame name={project.name} source={source} />
          </div>
        </div>
      </div>

      {manageOpened && (
        <div hidden={view !== 'manage'}>
          <ManagePanel
            projectId={id}
            ownerId={user?.id ?? ''}
            definition={definition}
            site={site}
            chatBusy={chatBusy}
            onBusyChange={setManageBusy}
            onAskInChat={askInChat}
          />
        </div>
      )}

      {/*
        * Outside the panes, so it is reachable in EVERY view. A build lands here in
        * preview mode, and while publishing lived inside the chat pane it was
        * display:none at phone width — the app was finished and there was no way to put
        * it on the internet without knowing to switch tabs first.
        */}
      <PublishPanel project={project} editing={editing} site={site} onSiteChange={setSite} />
      {/* Manage says all of this itself, with Add sign-in as a button; said twice, it is noise. */}
      {view !== 'manage' && (
        <>
          <section className="editor-people" aria-label="Sign-in">
            <p className="type-body">
              <strong>{signIn.headline}</strong> {signIn.detail}
            </p>
            {accounts === 'none' && (
              <p className="type-caption">{`To add it, ask in Chat: “${addSignInRequest(definition.plan, source)}”`}</p>
            )}
          </section>
          <p className="type-body editor-links">
            <Link to={`/submissions/${id}`}>See replies people sent</Link>
            {/*
              * Only once the site has a catalogue id: People is read from the server under it, and
              * an app that was never published has nobody to list.
              */}
            {definition.hostedAppId && (
              <>
                {' · '}
                <Link to={`/users/${definition.hostedAppId}`} state={{ accounts, reach }}>People using this site</Link>
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
}
